/**
 * Angebot zurück in die Kalkulation geben.
 *
 * Der Statuswechsel allein genügt nicht: Der Kalkulant hat den Vorgang längst
 * aus dem Blick, wenn das Angebot vor Wochen hinausging. Er muss erfahren,
 * warum es zurückkommt – und das weiß nur der, der mit dem Kunden gesprochen
 * hat. Kora kann es nicht erfinden, deshalb ist der Text Pflicht.
 *
 * Die Nachricht geht als Kommentar an den Vorgang und erwähnt den
 * Kalkulanten; Kora benachrichtigt ihn.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import type { Angebot, AngebotAntwort } from '../typen';

export function ZurueckModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState('');

  const speichern = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/status`, {
        status: 'START',
        notiz: text.trim(),
      }),
    onSuccess: () => {
      toastZeigen(`Zurück in die Kalkulation – ${angebot.kalkulant} ist informiert.`);
      onFertig();
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Wechsel fehlgeschlagen.'),
  });

  return (
    <Modal
      titel="Zurück in die Kalkulation"
      untertitel={angebot.name}
      breite={560}
      abstandOben={70}
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer
            onClick={() => speichern.mutate()}
            disabled={speichern.isPending || !text.trim()}
          >
            {speichern.isPending ? 'Wird gesetzt …' : 'Zurücksetzen und informieren'}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-hinweis">
        Der Vorgang geht auf START zurück und muss neu kalkuliert, geprüft und versendet werden.
      </div>

      <label className="modal-feld">
        <span>Nachricht an {angebot.kalkulant}</span>
        <div className="zurueck-erwaehnung">@{angebot.kalkulant}</div>
        <textarea
          className="modal-eingabe zurueck-text"
          rows={5}
          autoFocus
          placeholder="Was der Kunde geändert haben möchte – kurz, aber so, dass es ohne Rückfrage reicht."
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setFehler('');
          }}
        />
        <span className="modal-hinweis">
          Geht als Kommentar an den Vorgang. Kora benachrichtigt {angebot.kalkulant}.
        </span>
      </label>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}